import { useEffect, useRef, useState } from 'react';
import { Paperclip, Send } from 'lucide-react';
import type { TaskMember } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { deleteTaskAttachment } from '@/lib/tasks-api';
import { attachFilesToTask, sendMessage, uploadFile, withinSizeLimit } from './task-files';
import { MentionTextarea } from './MentionTextarea';
import { displayToMarkup, type PickedMention } from './mention-text';
import { clipboardFiles, pendingKey } from './clipboard-files';
import { PendingFiles, type PendingFile } from './PendingFiles';

/**
 * Chat box: `@` mentions, Cmd/Ctrl+Enter to send, attach files. Pasting or dropping a file in the
 * box stages it with the message; pasting anywhere else in the task posts it straight away
 * (dropping elsewhere is handled by TaskDetailView).
 */
export function TaskChatComposer({ taskId, members }: { taskId: string; members: TaskMember[] }) {
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState<PickedMention[]>([]);
  const [pending, setPending] = useState<PendingFile[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // A paste anywhere in this task (not already taken by the chat box) attaches the file.
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      if (event.defaultPrevented) return;
      const view = rootRef.current?.closest('[data-task-view]');
      const target = event.target as Node | null;
      // Inside the task, or focus on something that contains it (the dialog, or the page body).
      if (!view || !target || !(view.contains(target) || target.contains(view))) return;
      const files = clipboardFiles(event.clipboardData);
      if (!files.length) return;
      event.preventDefault();
      attachFilesToTask(taskId, files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [taskId]);

  /** Files start uploading as soon as they're added; Send doesn't wait for them. */
  const stage = (files: File[]) => {
    for (const file of withinSizeLimit(files)) {
      const key = pendingKey(file);
      const upload = uploadFile(taskId, file).then((attachmentId) => {
        setPending((p) =>
          attachmentId
            ? p.map((x) => (x.key === key ? { ...x, attachmentId, uploading: false } : x))
            : p.filter((x) => x.key !== key)
        );
        return attachmentId;
      });
      setPending((p) => [...p, { key, file, attachmentId: null, uploading: true, upload }]);
    }
  };

  const removePending = (item: PendingFile) => {
    setPending((p) => p.filter((x) => x.key !== item.key));
    void item.upload?.then((attachmentId) => {
      if (attachmentId) void deleteTaskAttachment(taskId, attachmentId);
    });
  };

  const canSend = text.trim().length > 0 || pending.length > 0;

  /** The message shows in the timeline at once; uploading and saving carry on behind it. */
  const send = () => {
    if (!canSend) return;
    sendMessage(taskId, displayToMarkup(text.trim(), mentions), pending);
    setText('');
    setMentions([]);
    setPending([]);
  };

  return (
    <div ref={rootRef} className="space-y-2 rounded-lg border bg-card p-2 shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/20">
      <MentionTextarea
        aria-label="Message"
        tagging
        value={text}
        onChange={setText}
        mentions={mentions}
        onMentionsChange={setMentions}
        members={members}
        rows={2}
        placeholder="Write a message… @ to mention"
        className="border-0 p-1 shadow-none focus-visible:ring-0"
        onSubmit={send}
        onFiles={stage}
      />
      <PendingFiles items={pending} onRemove={removePending} />
      <div className="flex items-center">
        <input
          ref={fileInput}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            e.target.value = '';
            if (files.length) stage(files);
          }}
        />
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          onClick={() => fileInput.current?.click()}
          aria-label="Attach files"
          data-hint="Attach files (or paste / drop them)"
        >
          <Paperclip className="size-4" />
        </Button>
        <span className="ml-auto mr-2 hidden text-xs text-muted-foreground sm:inline">
          {navigator.platform.toLowerCase().includes('mac') ? '⌘' : 'Ctrl'}+Enter to send
        </span>
        <Button size="sm" disabled={!canSend} onClick={send}>
          <Send className="size-4" />
          Send
        </Button>
      </div>
    </div>
  );
}
